/**
 * Derive alternative-path POI layouts from normal surface waypoints.
 * Authored `*AlternativeMinus1/2PointsOfInterest` in portal layout take precedence;
 * otherwise POIs are chosen from the normal path by mission index (boss is never dropped).
 *
 * Compact layout (same as arrival/cave/desert/fair): one draggable waypoint per active mission.
 * Each mission inherits the normal-route POI for that mission, so the boss stays on the same
 * map spot even when alt routes have fewer waypoints (e.g. The Scent alt −2 → 3 POIs).
 */
import { EXPLORE_UNKNOWN_PORTAL_LAYOUT } from '../exploreUnknownPortalLayout.js';
import { getBossMissionForMissionRange } from './exploreUnknownAlternativePath.js';

const SURFACE_POI_KEYS = {
  arrival: 'arrivalPointsOfInterest',
  cave: 'cavePointsOfInterest',
  desert: 'desertPointsOfInterest',
  fair: 'fairPointsOfInterest',
  lavender: 'lavenderPointsOfInterest',
  boat: 'boatPointsOfInterest',
  pixelpast: 'pixelPastPointsOfInterest',
  kendrex: 'kendrexPointsOfInterest',
  autobahn: 'autobahnPointsOfInterest',
  productionhell: 'productionHellPointsOfInterest',
  awakening: 'awakeningPointsOfInterest',
};

/** Portal layout key prefix per surface id (camelCase where needed). */
const SURFACE_LAYOUT_PREFIX = {
  arrival: 'arrival',
  cave: 'cave',
  desert: 'desert',
  fair: 'fair',
  lavender: 'lavender',
  boat: 'boat',
  pixelpast: 'pixelPast',
  kendrex: 'kendrex',
  autobahn: 'autobahn',
  productionhell: 'productionHell',
  awakening: 'awakening',
};

/**
 * Which missions to drop for alt −1 / −2 (deterministic; keep aligned with server plan rules).
 * When the surface has a post-boss finale (boss is not the last mission), alt −1 / −2 drop
 * middle missions only; boss stays penultimate and finale stays last (same shape as normal).
 * @param {number[]} normalMissionIndices
 * @param {1|2} missionsRemoved
 * @returns {Set<number>}
 */
export function pickAlternativeMissionDropSet(normalMissionIndices, missionsRemoved) {
  if (!Array.isArray(normalMissionIndices) || normalMissionIndices.length < 2) {
    throw new Error('pickAlternativeMissionDropSet: need at least 2 mission indices');
  }
  if (missionsRemoved !== 1 && missionsRemoved !== 2) {
    throw new Error(`pickAlternativeMissionDropSet: invalid missionsRemoved (${missionsRemoved})`);
  }
  const first = normalMissionIndices[0];
  const last = normalMissionIndices[normalMissionIndices.length - 1];
  const bossMission = getBossMissionForMissionRange(first, last);
  const hasPostBossFinale = bossMission != null && last !== bossMission;

  if (hasPostBossFinale) {
    const middles = normalMissionIndices.filter(
      (m) => m !== first && m !== bossMission && m !== last
    );
    const dropCount = Math.min(missionsRemoved, middles.length);
    const dropped = middles.slice(middles.length - dropCount);
    return new Set(dropped);
  }

  // Keep first and last (finale); drop from the middle only (deterministic: from the end).
  const middles = normalMissionIndices.filter((m) => m !== first && m !== last);
  const dropCount = Math.min(missionsRemoved, middles.length);
  const dropped = middles.slice(middles.length - dropCount);
  return new Set(dropped);
}

/**
 * Mirror server `buildSurfaceAlternativePlan` mission picking (deterministic).
 * @param {number[]} normalMissionIndices
 * @param {1|2} missionsRemoved
 * @returns {number[]}
 */
export function deriveAlternativeMissionIndices(normalMissionIndices, missionsRemoved) {
  const droppedSet = pickAlternativeMissionDropSet(normalMissionIndices, missionsRemoved);
  return normalMissionIndices.filter((m) => !droppedSet.has(m));
}

/**
 * @param {number[][]} normalPoints
 * @param {number[]} normalMissionIndices
 * @param {number[]} missionIndices
 * @returns {number[][]}
 */
export function mapPoisToMissionIndices(normalPoints, normalMissionIndices, missionIndices) {
  return missionIndices.map((missionIndex) => {
    const poiIdx = normalMissionIndices.indexOf(Number(missionIndex));
    if (poiIdx < 0) {
      throw new Error(
        `mapPoisToMissionIndices: mission ${missionIndex} not found on normal route for surface`
      );
    }
    const poi = normalPoints[poiIdx];
    if (!Array.isArray(poi) || poi.length !== 2) {
      throw new Error(`mapPoisToMissionIndices: invalid POI for mission ${missionIndex}`);
    }
    return [...poi];
  });
}

/**
 * Shift post-fork alternative nodes off the normal polyline (mission 1 stays shared).
 * @param {number[][]} pois
 * @param {{ branchOffsetX?: number, branchOffsetY?: number }} [opts]
 * @returns {number[][]}
 */
export function offsetAlternativeBranchPois(pois, opts = {}) {
  const branchOffsetX = Number(opts.branchOffsetX) || 0;
  const branchOffsetY = Number(opts.branchOffsetY) || 0;
  if (branchOffsetX === 0 && branchOffsetY === 0) {
    return pois.map((p) => [...p]);
  }
  return pois.map(([x, y], i) => {
    if (i === 0) {
      return [x, y];
    }
    return [x + branchOffsetX, y + branchOffsetY];
  });
}

/**
 * @param {number[][]} pois
 * @returns {number[]}
 */
export function derivePathForkPoint(pois) {
  if (!Array.isArray(pois) || pois.length < 2) {
    throw new Error('derivePathForkPoint: need at least 2 POIs');
  }
  const [a, b] = pois;
  return [Math.round((a[0] + b[0]) / 2), Math.round((a[1] + b[1]) / 2)];
}

/**
 * @param {string} surface
 * @param {1|2} missionsRemoved
 * @returns {string}
 */
function getAuthoredAltPoiLayoutKey(surface, missionsRemoved) {
  const prefix = SURFACE_LAYOUT_PREFIX[surface];
  if (!prefix) {
    throw new Error(`getAuthoredAltPoiLayoutKey: unsupported surface (${surface})`);
  }
  return missionsRemoved === 2
    ? `${prefix}AlternativeMinus2PointsOfInterest`
    : `${prefix}AlternativeMinus1PointsOfInterest`;
}

/**
 * @param {string} surface
 * @returns {string}
 */
function getAuthoredForkLayoutKey(surface) {
  const prefix = SURFACE_LAYOUT_PREFIX[surface];
  if (!prefix) {
    throw new Error(`getAuthoredForkLayoutKey: unsupported surface (${surface})`);
  }
  return `${prefix}PathForkPoint`;
}

/**
 * One POI per active mission; coords copied from the matching normal-route slot.
 * @param {string} surface
 * @param {number[][]} normalPoints
 * @param {number[]} normalMissionIndices
 * @param {number[]} activeMissionSequence
 * @returns {{ forkPoint: number[], points: number[][], missionIndices: number[], normalPoints: number[][] }}
 */
export function buildCompactAlternativeLayout(
  surface,
  normalPoints,
  normalMissionIndices,
  activeMissionSequence
) {
  const forkKey = getAuthoredForkLayoutKey(surface);
  const authoredFork = EXPLORE_UNKNOWN_PORTAL_LAYOUT[forkKey];
  const forkPoint = Array.isArray(authoredFork) && authoredFork.length === 2
    ? [...authoredFork]
    : derivePathForkPoint(normalPoints);
  const mappedPoints = mapPoisToMissionIndices(
    normalPoints,
    normalMissionIndices,
    activeMissionSequence
  );
  const points = offsetAlternativeBranchPois(mappedPoints, { branchOffsetX: -40, branchOffsetY: 110 });
  return {
    forkPoint,
    points,
    missionIndices: activeMissionSequence.map(Number),
    normalPoints,
  };
}

/**
 * @param {string} surface
 * @param {number[][]} normalPoints
 * @param {number[]} normalMissionIndices
 * @param {1|2} missionsRemoved
 * @param {number[]} missionIndices
 * @returns {{ points: number[][], forkPoint: number[] }}
 */
function resolveAlternativePoints(
  surface,
  normalPoints,
  normalMissionIndices,
  missionsRemoved,
  missionIndices
) {
  const expectedLen = missionIndices.length;
  const altKey = getAuthoredAltPoiLayoutKey(surface, missionsRemoved);
  const forkKey = getAuthoredForkLayoutKey(surface);
  const authoredAlt = EXPLORE_UNKNOWN_PORTAL_LAYOUT[altKey];
  const authoredFork = EXPLORE_UNKNOWN_PORTAL_LAYOUT[forkKey];
  const forkPoint = Array.isArray(authoredFork) && authoredFork.length === 2
    ? authoredFork
    : derivePathForkPoint(normalPoints);

  if (Array.isArray(authoredAlt) && authoredAlt.length === expectedLen) {
    return { points: authoredAlt.map((p) => [...p]), forkPoint: [...forkPoint] };
  }

  const layout = buildCompactAlternativeLayout(
    surface,
    normalPoints,
    normalMissionIndices,
    missionIndices
  );
  return { points: layout.points, forkPoint: layout.forkPoint };
}

/** Mission indices for the full normal branch on a surface (independent of dev alt/fork POI edits). */
export function buildCanonicalSurfaceMissionIndices(surface, mapMissionOffset) {
  const key = SURFACE_POI_KEYS[surface];
  const normalPoints = EXPLORE_UNKNOWN_PORTAL_LAYOUT[key];
  const count = Array.isArray(normalPoints) ? normalPoints.length : 0;
  return Array.from({ length: count }, (_, i) => mapMissionOffset + 1 + i);
}

/**
 * @param {string} surface
 * @param {1|2} missionsRemoved
 * @returns {{ forkPoint: number[], points: number[][], missionIndices: number[], normalPoints: number[][] }}
 */
export function getAlternativePathDisplayLayout(surface, missionsRemoved, firstMissionIndex, normalMissionIndices) {
  const key = SURFACE_POI_KEYS[surface];
  if (!key) {
    throw new Error(`getAlternativePathDisplayLayout: unsupported surface (${surface})`);
  }
  const normalPoints = EXPLORE_UNKNOWN_PORTAL_LAYOUT[key];
  if (!Array.isArray(normalPoints) || normalPoints.length === 0) {
    throw new Error(`getAlternativePathDisplayLayout: missing POIs for ${surface}`);
  }
  if (normalMissionIndices.length !== normalPoints.length) {
    throw new Error(
      `getAlternativePathDisplayLayout: derived POI count mismatch on ${surface} (expected ${normalPoints.length} mission indices, got ${normalMissionIndices.length})`
    );
  }
  const missionIndices = deriveAlternativeMissionIndices(normalMissionIndices, missionsRemoved);
  const { points, forkPoint } = resolveAlternativePoints(
    surface,
    normalPoints,
    normalMissionIndices,
    missionsRemoved,
    missionIndices
  );
  if (points.length !== missionIndices.length) {
    throw new Error(
      `getAlternativePathDisplayLayout: mission/POI count mismatch on ${surface} (${missionIndices.length} vs ${points.length})`
    );
  }
  return { forkPoint, points, missionIndices, normalPoints };
}

export function getNormalPathDisplayLayout(surface, normalMissionIndices) {
  const key = SURFACE_POI_KEYS[surface];
  const normalPoints = EXPLORE_UNKNOWN_PORTAL_LAYOUT[key];
  const forkKey = getAuthoredForkLayoutKey(surface);
  const authoredFork = EXPLORE_UNKNOWN_PORTAL_LAYOUT[forkKey];
  const forkPoint = Array.isArray(authoredFork) && authoredFork.length === 2
    ? authoredFork
    : derivePathForkPoint(normalPoints);
  return {
    forkPoint,
    points: normalPoints,
    missionIndices: normalMissionIndices,
  };
}
